/**
 * Collaboration Sync Client
 * Client-side helpers for real-time collaboration in String Sauce DAW
 * 
 * Features:
 * - WebSocket connection management with auto-reconnect
 * - Operation batching and debouncing
 * - Feedback loop prevention
 * - Vector clock management
 * - State reconciliation
 */

// ============================================================================
// OPERATION TYPES
// ============================================================================

export const OperationType = {
  // Track operations
  TRACK_ADD: 'track_add',
  TRACK_DELETE: 'track_delete',
  TRACK_UPDATE: 'track_update',
  TRACK_MOVE: 'track_move',
  
  // Clip operations
  CLIP_ADD: 'clip_add',
  CLIP_DELETE: 'clip_delete',
  CLIP_MOVE: 'clip_move',
  CLIP_RESIZE: 'clip_resize',
  CLIP_UPDATE: 'clip_update',
  
  // Plugin operations
  PLUGIN_ADD: 'plugin_add',
  PLUGIN_DELETE: 'plugin_delete',
  PLUGIN_MOVE: 'plugin_move',
  PLUGIN_PARAM_CHANGE: 'plugin_param_change',
  PLUGIN_BYPASS_TOGGLE: 'plugin_bypass_toggle',
  PLUGIN_PRESET_LOAD: 'plugin_preset_load',
  
  // Automation operations
  AUTOMATION_LANE_ADD: 'automation_lane_add',
  AUTOMATION_LANE_DELETE: 'automation_lane_delete',
  AUTOMATION_POINT_ADD: 'automation_point_add',
  AUTOMATION_POINT_DELETE: 'automation_point_delete',
  AUTOMATION_POINT_MOVE: 'automation_point_move',
  AUTOMATION_PARAM_CHANGE: 'automation_param_change',
  
  // Project operations
  BPM_CHANGE: 'bpm_change',
  TIME_SIGNATURE_CHANGE: 'time_signature_change',
  MASTER_VOLUME_CHANGE: 'master_volume_change',
  PROJECT_SETTINGS_UPDATE: 'project_settings_update',
  
  // Session operations
  CURSOR_MOVE: 'cursor_move',
  TRACK_SELECT: 'track_select',
  TRANSPORT: 'transport',
  CHAT: 'chat',
  
  // State sync
  SYNC_STATE: 'sync_state',
};


// ============================================================================
// VECTOR CLOCK
// ============================================================================

export class VectorClock {
  constructor(clock = {}) {
    this.clock = { ...clock };
  }
  
  increment(nodeId) {
    const newClock = new VectorClock(this.clock);
    newClock.clock[nodeId] = (this.clock[nodeId] || 0) + 1;
    return newClock;
  }
  
  merge(other) {
    const newClock = new VectorClock();
    const allNodes = new Set([...Object.keys(this.clock), ...Object.keys(other.clock)]);
    
    for (const node of allNodes) {
      newClock.clock[node] = Math.max(
        this.clock[node] || 0,
        other.clock[node] || 0
      );
    }
    
    return newClock;
  }
  
  happensBefore(other) {
    const allNodes = new Set([...Object.keys(this.clock), ...Object.keys(other.clock)]);
    let atLeastOneLess = false;
    
    for (const node of allNodes) {
      const selfVal = this.clock[node] || 0;
      const otherVal = other.clock[node] || 0;
      
      if (selfVal > otherVal) return false;
      if (selfVal < otherVal) atLeastOneLess = true;
    }
    
    return atLeastOneLess;
  }
  
  toJSON() {
    return this.clock;
  }
}


// ============================================================================
// OPERATION DEBOUNCER
// ============================================================================

class OperationDebouncer {
  constructor(delay = 50) {
    this.delay = delay;
    this.pending = new Map(); // path -> { timer, data }
  }
  
  /**
   * Debounce an operation by path
   * Operations targeting the same path within `delay` ms are merged
   */
  debounce(path, data, callback) {
    const pathKey = path.join('/');
    
    // Clear existing timer
    if (this.pending.has(pathKey)) {
      clearTimeout(this.pending.get(pathKey).timer);
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      this.pending.delete(pathKey);
      callback(data);
    }, this.delay);
    
    this.pending.set(pathKey, { timer, data });
  }
  
  /**
   * Flush all pending operations immediately
   */
  flush(callback) {
    for (const [pathKey, { timer, data }] of this.pending) {
      clearTimeout(timer);
      callback(data);
    }
    this.pending.clear();
  }
  
  /**
   * Cancel all pending operations
   */
  clear() {
    for (const [, { timer }] of this.pending) {
      clearTimeout(timer);
    }
    this.pending.clear();
  }
}


// ============================================================================
// COLLABORATION SYNC CLIENT
// ============================================================================

export class CollaborationSyncClient {
  constructor(options = {}) {
    this.projectId = options.projectId || null;
    this.userId = options.userId || null;
    this.userName = options.userName || 'Anonymous';
    this.backendUrl = options.backendUrl || process.env.REACT_APP_BACKEND_URL || '';
    
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000;
    
    // State tracking
    this.localClock = new VectorClock();
    this.stateVersion = 0;
    this.pendingOperations = new Map(); // operationId -> operation
    
    // Feedback loop prevention
    this.remoteOperationIds = new Set();
    this.isApplyingRemoteUpdate = false;
    
    // Debouncing
    this.debouncer = new OperationDebouncer(options.debounceDelay || 50);
    
    // Callbacks
    this.onConnect = options.onConnect || (() => {});
    this.onDisconnect = options.onDisconnect || (() => {});
    this.onUserJoin = options.onUserJoin || (() => {});
    this.onUserLeave = options.onUserLeave || (() => {});
    this.onRemoteOperation = options.onRemoteOperation || (() => {});
    this.onCursorUpdate = options.onCursorUpdate || (() => {});
    this.onTrackSelection = options.onTrackSelection || (() => {});
    this.onChatMessage = options.onChatMessage || (() => {});
    this.onStateSync = options.onStateSync || (() => {});
    this.onError = options.onError || (() => {});
  }
  
  // -------------------------------------------------------------------------
  // CONNECTION MANAGEMENT
  // -------------------------------------------------------------------------
  
  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }
    
    return new Promise((resolve, reject) => {
      try {
        const wsUrl = this.backendUrl.replace(/^http/, 'ws');
        const url = `${wsUrl}/api/collaboration/ws/${this.projectId}/${this.userId}?user_name=${encodeURIComponent(this.userName)}`;
        
        this.ws = new WebSocket(url);
        
        this.ws.onopen = () => {
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.onConnect();
          resolve();
        };
        
        this.ws.onclose = (event) => {
          this.isConnected = false;
          this.onDisconnect();
          
          // Auto-reconnect if not intentional close
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };
        
        this.ws.onerror = (error) => {
          console.error('[CollabSync] WebSocket error', error);
          this.onError(error);
          reject(error);
        };
        
        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (err) {
            console.error('[CollabSync] Failed to parse message', err);
          }
        };
        
      } catch (error) {
        reject(error);
      }
    });
  }
  
  disconnect() {
    this.debouncer.clear();
    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
    this.isConnected = false;
  }
  
  scheduleReconnect() {
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000 // Max 30 seconds
    );
    
    
    setTimeout(() => {
      this.reconnectAttempts++;
      this.connect().catch(() => {});
    }, delay);
  }
  
  // -------------------------------------------------------------------------
  // MESSAGE HANDLING
  // -------------------------------------------------------------------------
  
  handleMessage(data) {
    const { type } = data;
    
    switch (type) {
      case 'init':
        this.handleInit(data);
        break;
      
      case 'user_joined':
        this.onUserJoin(data.user, data.users);
        break;
      
      case 'user_left':
        this.onUserLeave(data.userId, data.users);
        break;
      
      case 'cursor_update':
        this.onCursorUpdate(data.userId, data.cursor);
        break;
      
      case 'track_selection':
        this.onTrackSelection(data.userId, data.trackId, data.userName);
        break;
      
      case 'chat_message':
        this.onChatMessage(data);
        break;
      
      case 'state_sync':
        this.handleStateSync(data);
        break;
      
      case 'operation_ack':
        this.handleOperationAck(data);
        break;
      
      case 'operation_rejected':
        this.handleOperationRejected(data);
        break;
      
      case 'transport_update':
        this.onRemoteOperation({ type: 'transport', ...data });
        break;
      
      default:
        // Handle operation applied messages
        if (type.endsWith('_applied')) {
          this.handleRemoteOperation(data);
        }
    }
  }
  
  handleInit(data) {
    
    if (data.state) {
      this.stateVersion = data.state.version || 0;
      this.localClock = new VectorClock(data.state.vectorClock || {});
      this.onStateSync(data.state);
    }
    
    if (data.users) {
      this.onUserJoin(null, data.users);
    }
  }
  
  handleStateSync(data) {
    if (data.state) {
      this.stateVersion = data.state.version || 0;
      this.localClock = new VectorClock(data.state.vectorClock || {});
      this.onStateSync(data.state);
    }
  }
  
  handleRemoteOperation(data) {
    const { operation, stateVersion } = data;
    
    if (!operation) return;
    
    // Skip if this is our own operation (feedback loop prevention)
    if (operation.userId === this.userId) return;
    if (this.remoteOperationIds.has(operation.id)) return;
    
    // Track this operation to prevent re-processing
    this.remoteOperationIds.add(operation.id);
    
    // Clean up old operation IDs (keep last 1000)
    if (this.remoteOperationIds.size > 1000) {
      const ids = Array.from(this.remoteOperationIds);
      this.remoteOperationIds = new Set(ids.slice(-500));
    }
    
    // Update state version
    if (stateVersion) {
      this.stateVersion = stateVersion;
    }
    
    // Merge vector clock
    if (operation.vectorClock) {
      this.localClock = this.localClock.merge(new VectorClock(operation.vectorClock));
    }
    
    // Mark as remote update to prevent feedback
    this.isApplyingRemoteUpdate = true;
    
    try {
      this.onRemoteOperation(operation);
    } finally {
      this.isApplyingRemoteUpdate = false;
    }
  }
  
  handleOperationAck(data) {
    const { operationId, stateVersion } = data;
    
    // Remove from pending
    this.pendingOperations.delete(operationId);
    
    // Update state version
    if (stateVersion) {
      this.stateVersion = stateVersion;
    }
  }
  
  handleOperationRejected(data) {
    console.error('[CollabSync] Operation rejected:', data);
    this.onError(new Error(`Operation rejected: ${data.reason}`));
  }
  
  // -------------------------------------------------------------------------
  // SEND OPERATIONS
  // -------------------------------------------------------------------------
  
  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return true;
    }
    return false;
  }
  
  /**
   * Send an operation with automatic debouncing and feedback loop prevention
   */
  sendOperation(type, path, data, options = {}) {
    // Don't send if we're applying a remote update (feedback loop prevention)
    if (this.isApplyingRemoteUpdate && !options.force) {
      return false;
    }
    
    const operation = {
      type,
      path,
      data,
      id: `${this.userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    
    if (options.debounce !== false && options.debounceDelay !== 0) {
      this.debouncer.debounce(path, operation, (op) => {
        this.pendingOperations.set(op.id, op);
        this.send(op);
      });
    } else {
      this.pendingOperations.set(operation.id, operation);
      this.send(operation);
    }
    
    return true;
  }
  
  // -------------------------------------------------------------------------
  // HIGH-LEVEL OPERATION METHODS
  // -------------------------------------------------------------------------
  
  // Track operations
  sendTrackAdd(track) {
    return this.sendOperation(OperationType.TRACK_ADD, ['tracks'], { track }, { debounce: false });
  }
  
  sendTrackDelete(trackId) {
    return this.sendOperation(OperationType.TRACK_DELETE, ['tracks', trackId], { trackId }, { debounce: false });
  }
  
  sendTrackUpdate(trackId, changes) {
    return this.sendOperation(OperationType.TRACK_UPDATE, ['tracks', trackId], { trackId, changes });
  }
  
  sendTrackMove(trackId, newIndex) {
    return this.sendOperation(OperationType.TRACK_MOVE, ['tracks', trackId], { trackId, newIndex }, { debounce: false });
  }
  
  // Clip operations
  sendClipAdd(trackId, clip) {
    return this.sendOperation(OperationType.CLIP_ADD, ['tracks', trackId, 'clips'], { trackId, clip }, { debounce: false });
  }
  
  sendClipDelete(trackId, clipId) {
    return this.sendOperation(OperationType.CLIP_DELETE, ['tracks', trackId, 'clips', clipId], { trackId, clipId }, { debounce: false });
  }
  
  sendClipMove(trackId, clipId, newStartTime) {
    return this.sendOperation(OperationType.CLIP_MOVE, ['tracks', trackId, 'clips', clipId], { trackId, clipId, newStartTime });
  }
  
  // Plugin operations
  sendPluginAdd(trackId, plugin) {
    const path = trackId === 'master' 
      ? ['master', 'pluginChain'] 
      : ['tracks', trackId, 'pluginChain'];
    return this.sendOperation(OperationType.PLUGIN_ADD, path, { trackId, plugin }, { debounce: false });
  }
  
  sendPluginDelete(trackId, pluginId) {
    const path = trackId === 'master'
      ? ['master', 'pluginChain', pluginId]
      : ['tracks', trackId, 'pluginChain', pluginId];
    return this.sendOperation(OperationType.PLUGIN_DELETE, path, { trackId, pluginId }, { debounce: false });
  }
  
  sendPluginParamChange(trackId, pluginId, paramId, value) {
    const path = trackId === 'master'
      ? ['master', 'pluginChain', pluginId, 'params', paramId]
      : ['tracks', trackId, 'pluginChain', pluginId, 'params', paramId];
    return this.sendOperation(OperationType.PLUGIN_PARAM_CHANGE, path, { trackId, pluginId, paramId, value });
  }
  
  sendPluginBypassToggle(trackId, pluginId, enabled) {
    const path = trackId === 'master'
      ? ['master', 'pluginChain', pluginId]
      : ['tracks', trackId, 'pluginChain', pluginId];
    return this.sendOperation(OperationType.PLUGIN_BYPASS_TOGGLE, path, { trackId, pluginId, enabled }, { debounce: false });
  }
  
  // Automation operations
  sendAutomationLaneAdd(trackId, lane) {
    return this.sendOperation(OperationType.AUTOMATION_LANE_ADD, ['tracks', trackId, 'automationLanes'], { trackId, lane }, { debounce: false });
  }
  
  sendAutomationPointAdd(trackId, laneId, point) {
    return this.sendOperation(OperationType.AUTOMATION_POINT_ADD, ['tracks', trackId, 'automationLanes', laneId, 'points'], { trackId, laneId, point });
  }
  
  sendAutomationPointMove(trackId, laneId, pointId, time, value) {
    return this.sendOperation(OperationType.AUTOMATION_POINT_MOVE, ['tracks', trackId, 'automationLanes', laneId, 'points', pointId], { trackId, laneId, pointId, time, value });
  }
  
  // Project operations
  sendBpmChange(bpm) {
    return this.sendOperation(OperationType.BPM_CHANGE, ['project', 'bpm'], { bpm });
  }
  
  sendMasterVolumeChange(masterVolume) {
    return this.sendOperation(OperationType.MASTER_VOLUME_CHANGE, ['project', 'masterVolume'], { masterVolume });
  }
  
  // Session operations (non-debounced)
  sendCursorMove(cursor) {
    return this.send({ type: OperationType.CURSOR_MOVE, cursor });
  }
  
  sendTrackSelect(trackId) {
    return this.send({ type: OperationType.TRACK_SELECT, trackId });
  }
  
  sendTransport(action, position) {
    return this.send({ type: OperationType.TRANSPORT, action, position });
  }
  
  sendChat(message) {
    return this.send({ type: OperationType.CHAT, message, userName: this.userName });
  }
  
  // Full state sync
  sendStateSync(state) {
    return this.send({ type: OperationType.SYNC_STATE, state });
  }
  
  // -------------------------------------------------------------------------
  // HELPERS
  // -------------------------------------------------------------------------
  
  /**
   * Check if currently applying a remote update
   * Use this to prevent feedback loops in state update handlers
   */
  isRemoteUpdate() {
    return this.isApplyingRemoteUpdate;
  }
  
  /**
   * Flush all pending debounced operations
   */
  flushPending() {
    this.debouncer.flush((op) => {
      this.pendingOperations.set(op.id, op);
      this.send(op);
    });
  }
  
  /**
   * Get current connection status
   */
  getStatus() {
    return {
      connected: this.isConnected,
      pendingOperations: this.pendingOperations.size,
      stateVersion: this.stateVersion
    };
  }
}


// ============================================================================
// REACT HOOK
// ============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * React hook for collaboration sync
 */
export function useCollaborationSync({
  projectId,
  userId,
  userName,
  enabled = true,
  onStateUpdate,
  onUserUpdate,
  onChatMessage,
}) {
  const [isConnected, setIsConnected] = useState(false);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  
  const clientRef = useRef(null);
  const onStateUpdateRef = useRef(onStateUpdate);
  onStateUpdateRef.current = onStateUpdate;
  
  // Initialize client
  useEffect(() => {
    if (!enabled || !projectId || !userId) return;
    
    const client = new CollaborationSyncClient({
      projectId,
      userId,
      userName,
      
      onConnect: () => {
        setIsConnected(true);
        setError(null);
      },
      
      onDisconnect: () => {
        setIsConnected(false);
      },
      
      onUserJoin: (user, allUsers) => {
        setUsers(allUsers || []);
        onUserUpdate?.(user, allUsers);
      },
      
      onUserLeave: (userId, allUsers) => {
        setUsers(allUsers || []);
        onUserUpdate?.(null, allUsers);
      },
      
      onRemoteOperation: (operation) => {
        onStateUpdateRef.current?.(operation);
      },
      
      onStateSync: (state) => {
        onStateUpdateRef.current?.({ type: 'full_sync', state });
      },
      
      onChatMessage: (message) => {
        onChatMessage?.(message);
      },
      
      onError: (err) => {
        setError(err);
      },
    });
    
    clientRef.current = client;
    client.connect().catch(setError);
    
    return () => {
      client.disconnect();
      clientRef.current = null;
    };
  }, [enabled, projectId, userId, userName]);
  
  // Send methods
  const sendTrackUpdate = useCallback((trackId, changes) => {
    clientRef.current?.sendTrackUpdate(trackId, changes);
  }, []);
  
  const sendPluginParamChange = useCallback((trackId, pluginId, paramId, value) => {
    clientRef.current?.sendPluginParamChange(trackId, pluginId, paramId, value);
  }, []);
  
  const sendBpmChange = useCallback((bpm) => {
    clientRef.current?.sendBpmChange(bpm);
  }, []);
  
  const sendCursorMove = useCallback((cursor) => {
    clientRef.current?.sendCursorMove(cursor);
  }, []);
  
  const sendChat = useCallback((message) => {
    clientRef.current?.sendChat(message);
  }, []);
  
  const isRemoteUpdate = useCallback(() => {
    return clientRef.current?.isRemoteUpdate() || false;
  }, []);
  
  return {
    isConnected,
    users,
    error,
    client: clientRef.current,
    sendTrackUpdate,
    sendPluginParamChange,
    sendBpmChange,
    sendCursorMove,
    sendChat,
    isRemoteUpdate,
  };
}


export default CollaborationSyncClient;
